import { Box, Typography, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import { usePost } from 'hooks/use-post';
import React from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import Collapsible from 'components/collapsible';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Switch from 'components/switch';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { getManagementSilos, putDisableSilo } from '../api/silo-management';
import { DisabledSilo } from '../types/silo-management-form.type';

type ParamTypes = {
  globalSiteId: string;
};

type Props = {};

const PAGE_NAME_ID = 'siteSettings.editSiteSettings.siloManagement';

const SiloManagement: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();

  // #region GET DATA
  const { data: silos } = useGet(
    ['getManagementSilos', params.globalSiteId],
    (context) => getManagementSilos(String(context.queryKey[1])),
    { enabled: !!params.globalSiteId }
  );
  // #endregion GET DATA

  // #region PUT DATA

  const updateSiloState = usePost<AxiosResponse, DisabledSilo>(
    (request) => putDisableSilo(params.globalSiteId, request.siloId, request.status),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getManagementSilos', params.globalSiteId],
        exact: true
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  // #endregion PUT DATA

  // #region OPTIONS
  const listOptions: HeadCell[] = [
    { id: 'name', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.siloName` }) },
    { id: 'fleetName', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.fleetName` }) },
    { id: 'description', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.description` }) },
    {
      id: 'capacity',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.capacity` }),
      align: 'left',
    },
    {
      id: 'enabled',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.disabled` }),
      align: 'right',
    },
  ];
  // #endregion OPTIONS

  return (
    <Collapsible hideBorder label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })}>
      <List
        dark
        sx={{
          '&& .Table-tableHeadCell': {
            color: `${theme.palette.primary.main} !important`,
          },
          '&:hover': {
            '&& .Table-tableHeadCell': {
              color: `${theme.palette.primary.main} !important`,
            },
          },
        }}
        options={listOptions}>
        {silos
          ?.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
          .filter((sl) => !sl.isExtraStorage)
          .map((field, idx) => (
            <ListRow key={field.id}>
              <ListCell>
                <Typography marginRight={5} noWrap>
                  {field.name}
                </Typography>
              </ListCell>
              <ListCell>
                <Typography noWrap>{`${field.fleetName} (${field.fleetId})`}</Typography>
              </ListCell>
              <ListCell>
                <Typography noWrap>{field.description}</Typography>
              </ListCell>
              <ListCell>
                <FormattedNumber value={Number(field.capacity)} maximumFractionDigits={2} unit={'kilogram'} style="unit" />
              </ListCell>
              <ListCell align="right">
                <Box display={'flex'} justifyContent="flex-end">
                  <Switch
                    label=""
                    dark
                    key={idx}
                    value={!field.isEnabled}
                    disabled={updateSiloState.isPending}
                    onChange={() => updateSiloState.mutate({ siloId: field.id, status: !field.isEnabled })}
                  />
                </Box>
              </ListCell>
            </ListRow>
          ))}
      </List>
    </Collapsible>
  );
};

export default React.memo(SiloManagement);
